
import { useCallback, useState } from 'react';

export interface GeolocationOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
}

export const useGeolocationService = () => {
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get user's current location. Memoized with a stable identity (no
  // external deps — it only touches state setters, which React guarantees
  // are stable) so callers that depend on it in their own useCallback/
  // useEffect deps don't get a new function every render.
  const getUserLocation = useCallback((options: GeolocationOptions = {}) => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return Promise.reject('Geolocation not supported');
    }

    setIsGettingLocation(true);

    const defaultOptions: GeolocationOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
      ...options
    };

    return new Promise<[number, number]>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location: [number, number] = [
            position.coords.longitude,
            position.coords.latitude
          ];
          setUserLocation(location);
          setIsGettingLocation(false);
          resolve(location);
        },
        (err) => {
          setError(`Error getting location: ${err.message}`);
          setIsGettingLocation(false);
          reject(err);
        },
        defaultOptions
      );
    });
  }, []);

  return {
    userLocation,
    isGettingLocation,
    getUserLocation,
    error,
    setError
  };
};
